/**
 * Utility functions for consistent date/time handling across the application
 */

/**
 * Formats a date string to local timezone with proper formatting
 * @param {string|Date} dateString - The date string or Date object to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in local timezone
 */
export const formatLocalDateTime = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };
    
    const formatOptions = { ...defaultOptions, ...options };
    
    return date.toLocaleString('en-US', formatOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to local date only (no time)
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted date string in local timezone
 */
export const formatLocalDate = (dateString) => {
  return formatLocalDateTime(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats a calendar date (YYYY-MM-DD) without timezone conversion.
 * Use for scheduled_date and other date-only fields from the API.
 */
export const formatCalendarDate = (dateString, options = {}) => {
  if (!dateString) return null;

  try {
    const datePart = String(dateString).split('T')[0];
    const [year, month, day] = datePart.split('-').map((num) => parseInt(num, 10));
    if (!year || !month || !day) return null;

    const localDate = new Date(year, month - 1, day);
    if (isNaN(localDate.getTime())) return null;

    return localDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options
    });
  } catch (error) {
    console.error('Error formatting calendar date:', error);
    return null;
  }
};

/**
 * Formats a date string to local time only (no date)
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted time string in local timezone
 */
export const formatLocalTime = (dateString) => {
  return formatLocalDateTime(dateString, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

/**
 * Gets the current timezone offset in hours
 * @returns {number} Timezone offset in hours
 */
export const getTimezoneOffset = () => {
  return new Date().getTimezoneOffset() / -60;
};

/**
 * Converts a UTC date string to local timezone
 * @param {string} utcDateString - UTC date string
 * @returns {Date} Date object in local timezone
 */
export const utcToLocal = (utcDateString) => {
  if (!utcDateString) return null;
  
  try {
    // If the string already has timezone info, parse it directly
    if (utcDateString.includes('GMT') || utcDateString.includes('+') || utcDateString.includes('Z')) {
      return new Date(utcDateString);
    }
    
    // If it's a plain ISO string, treat it as UTC
    if (utcDateString.includes('T') && !utcDateString.includes('Z') && !utcDateString.includes('+')) {
      return new Date(utcDateString + 'Z');
    }
    
    // Otherwise, parse as-is
    return new Date(utcDateString);
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return null;
  }
};

/**
 * Parses a pasted date string into YYYY-MM-DD format
 * Supports MM/DD/YYYY, DD/MM/YYYY (if day > 12), YYYY/MM/DD, and ISO formats
 * @param {string} text - The pasted text to parse
 * @returns {string|null} Date in YYYY-MM-DD format, or null if parsing fails
 */
export const parsePastedDate = (text) => {
  if (!text || typeof text !== 'string') return null;
  
  const trimmed = text.trim();
  
  // Already in YYYY-MM-DD format
  const isoFormat = /^(\d{4})-(\d{2})-(\d{2})$/;
  let match = trimmed.match(isoFormat);
  if (match) return trimmed;
  
  // Try MM/DD/YYYY or MM-DD-YYYY (US format)
  const usFormat = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
  match = trimmed.match(usFormat);
  if (match) {
    const [, month, day, year] = match.map(Number);
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  
  // Try DD/MM/YYYY or DD-MM-YYYY (European format - only if day > 12)
  const euFormat = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
  match = trimmed.match(euFormat);
  if (match) {
    const [, day, month, year] = match.map(Number);
    if (day > 12 || month > 12) {
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }
  
  // Try YYYY/MM/DD
  const isoSlashFormat = /^(\d{4})[\/\-](\d{2})[\/\-](\d{2})$/;
  match = trimmed.match(isoSlashFormat);
  if (match) {
    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  
  // Try to parse with Date constructor as last resort
  const parsedDate = new Date(trimmed);
  if (!isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  return null;
};

/**
 * Creates a paste handler for date input fields
 * Intercepts paste events and converts pasted text into YYYY-MM-DD format
 * @param {Function} onChange - The onChange handler for the input
 * @returns {Function} Paste event handler
 */
export const createDatePasteHandler = (onChange) => {
  return (e) => {
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const parsedDate = parsePastedDate(pastedText);
    
    if (parsedDate) {
      e.preventDefault();
      const syntheticEvent = {
        target: {
          value: parsedDate
        }
      };
      onChange(syntheticEvent);
    }
    // If parsing fails, let the default paste behavior occur
  };
};

